import { Link, useNavigate } from "react-router";

import { Badge } from "@/components/ui/badge";
import type { CaseSummary } from "@/lib/api";

import { StageBadge } from "./stage-badge";

interface CaseTableProps {
  cases: CaseSummary[];
}

/** Notion-style table view of the cases library. Rows link to the detail page. */
export function CaseTable({ cases }: CaseTableProps) {
  const navigate = useNavigate();

  return (
    <div className="border-border overflow-x-auto rounded-lg border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-border bg-primary/5 border-b text-left">
            <th className="text-primary px-4 py-3 font-semibold">Case name</th>
            <th className="text-primary px-4 py-3 font-semibold">Client</th>
            <th className="text-primary px-4 py-3 font-semibold">Industry</th>
            <th className="text-primary px-4 py-3 font-semibold">Service line</th>
            <th className="text-primary px-4 py-3 font-semibold">Region</th>
            <th className="text-primary px-4 py-3 font-semibold">Year</th>
            <th className="text-primary px-4 py-3 font-semibold">Stage</th>
          </tr>
        </thead>
        <tbody className="divide-border divide-y">
          {cases.map((item) => (
            <tr
              key={item.id}
              className="hover:bg-accent-light/30 cursor-pointer transition-colors"
              onClick={() => void navigate(`/cases/${item.id}`)}
            >
              <td className="max-w-md px-4 py-3">
                <Link
                  to={`/cases/${item.id}`}
                  className="text-primary hover:text-accent font-bold underline-offset-4 hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  {item.slide_title}
                </Link>
              </td>
              <td className="px-4 py-3 whitespace-nowrap">{item.client_name}</td>
              <td className="px-4 py-3">
                <div className="flex flex-wrap gap-1">
                  {item.industry.map((i) => (
                    <Badge key={i} variant="secondary">
                      {i}
                    </Badge>
                  ))}
                </div>
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-wrap gap-1">
                  {item.service_lines.map((s) => (
                    <Badge key={s} variant="outline">
                      {s}
                    </Badge>
                  ))}
                </div>
              </td>
              <td className="text-muted px-4 py-3 whitespace-nowrap">{item.region ?? "—"}</td>
              <td className="text-muted px-4 py-3 whitespace-nowrap">{item.year ?? "—"}</td>
              <td className="px-4 py-3 whitespace-nowrap">
                <StageBadge stage={item.stage} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
